"use client";

import { useState } from "react";
import { Switch } from "antd";
import { useCookieConsent } from "../../providers/CookieConsentProvider";

export default function CookieBanner() {
  const {
    consent,
    acceptAll,
    essentialOnly,
    savePreferences,
    showConsentBanner,
    showPreferencesPanel,
    setShowPreferencesPanel,
  } = useCookieConsent();

  const [localAnalytics, setLocalAnalytics] = useState(consent.analytics);
  const [localMarketing, setLocalMarketing] = useState(consent.marketing);

  // Don't show banner if not needed
  if (!showConsentBanner) return null;

  const handleSave = () => {
    savePreferences(localAnalytics, localMarketing);
  };

  return (
    <>
      {/* COOKIE CONSENT BANNER - Exact same layout as before */}
      {!showPreferencesPanel && (
        <div className="fixed bottom-2 left-1/2 transform -translate-x-1/2 bg-white shadow-xl z-9999 border border-gray-200 rounded-2xl w-[calc(100vw-20px)] max-w-87.5 min-[426px]:w-[95%] min-[426px]:max-w-300">
          <div className="p-3 sm:p-4 md:p-6 max-w-none sm:max-w-7xl mx-auto">
            <div className="flex flex-col gap-3 sm:gap-4 md:flex-row md:justify-between md:items-start md:gap-6">
              <div className="flex-1 w-full">
                <h2 className="text-base sm:text-lg md:text-xl font-openSans font-semibold mb-2 text-gray-800">
                  Cookie Preferences
                </h2>
                <p className="text-xs sm:text-sm font-openSans text-[#333] mb-3 leading-relaxed">
                  {` We use cookies to keep your session running smoothly,
                  understand how you use our Platform, and send relevant emails
                  if you've opted in to marketing. You can choose which cookies
                  to allow.`}
                </p>

                <div
                  className="text-xs text-blue-600 hover:underline cursor-pointer"
                  onClick={() => setShowPreferencesPanel(true)}
                >
                  Cookie Policy
                </div>
              </div>

              <div className="flex flex-col gap-2 w-full md:w-auto md:items-end">
                <button
                  onClick={() => setShowPreferencesPanel(true)}
                  className="text-xs sm:text-sm font-openSans text-blue-600 hover:text-blue-800 underline text-left md:text-right"
                >
                  Manage Preferences
                </button>

                <div className="flex flex-col sm:flex-row gap-2 w-full">
                  <button
                    onClick={essentialOnly}
                    className="px-3 py-2 text-xs sm:text-sm font-openSans font-semibold bg-gray-200 text-blue-600 rounded-md border border-gray-300 hover:bg-gray-300 transition duration-200 flex items-center justify-center min-h-9"
                  >
                    Essential Only
                  </button>

                  <button
                    onClick={acceptAll}
                    className="px-3 py-2 text-xs sm:text-sm bg-blue-600 font-openSans font-semibold text-white rounded-md hover:bg-blue-700 transition duration-200 flex items-center justify-center min-h-9"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MANAGE PREFERENCES PANEL - Exact same design as your previous code */}
      {showPreferencesPanel && (
        <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-1 text-gray-800">
                Cookie Preferences
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                Choose which cookies you want to allow. You can change these
                settings at any time using the Cookie Preferences link in the
                footer.
              </p>

              {/* Essential - Always Active (Disabled) */}
              <div className="flex items-center justify-between border-b pb-4 mb-4">
                <div className="pr-4 flex-1">
                  <p className="font-medium font-openSans text-sm text-gray-800">
                    Essential Cookies
                  </p>
                  <p className="text-xs text-gray-500">
                    These cookies are strictly necessary for the Platform to
                    work. They keep your session running and remember where you
                    are in your Drive. They cannot be turned off.
                  </p>
                </div>
                <Switch
                  checked
                  disabled
                  className="data-[state=checked]:bg-blue-600"
                />
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between border-b pb-4 mb-4">
                <div className="pr-4 flex-1">
                  <p className="font-medium font-openSans text-sm text-gray-800">
                    Analytics Cookies — Optional
                  </p>
                  <p className="text-xs text-gray-500">
                    These cookies help us understand how you use the Platform so
                    we can improve it. We use Google Analytics to collect this
                    data. No personal information is shared with third parties
                    for advertising purposes.
                  </p>
                </div>
                <Switch
                  checked={localAnalytics}
                  onChange={(checked) => setLocalAnalytics(checked)}
                  className="data-[state=checked]:bg-blue-600"
                />
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between pb-4">
                <div className="pr-4 flex-1">
                  <p className="font-medium font-openSans text-sm text-gray-800">
                    Marketing Cookies — Optional
                  </p>
                  <p className="text-xs text-gray-500">
                    {`These cookies track how you interact with the Platform so we
                    can send you relevant emails — but only if you've agreed to
                    receive marketing communications from us. We do not use
                    these cookies to show you ads on other websites.`}
                  </p>
                </div>
                <Switch
                  checked={localMarketing}
                  onChange={(checked) => setLocalMarketing(checked)}
                  className="data-[state=checked]:bg-blue-600"
                />
              </div>
            </div>

            <div className="border-t p-4 flex gap-3">
              <button
                onClick={() => setShowPreferencesPanel(false)}
                className="cancelButton flex-1 border border-[#ddd] rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700"
              >
                Save My Preferences
              </button>
            </div>

            <div className="text-center pb-4">
              <a
                href="/cookie-policy"
                target="_blank"
                className="text-blue-600 hover:underline text-xs"
              >
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
